// libraries
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { debounce } from 'lodash'

// services
import { useCaptureImageMutation } from 'services/assets'

// utils
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import { trackEvent } from 'utils/tracking'

export const useCaptureMultipleImages = () => {
  const [captureImage, { isLoading, error }] = useCaptureImageMutation()
  const { selectedCameraIds, scaleCameras } = useSelector(state => state.devices)

  const requestImages = async (callback, fileNames = [], scaleId) => {
    try {
      // Determine which cameras to use based on scaleId
      const cameraIds = scaleId && scaleCameras[scaleId]?.length ? scaleCameras[scaleId] : selectedCameraIds

      const imageData = await cameraIds.reduce(async (prevPromise, cameraId) => {
        let filename = fileNames?.find(fileName => fileName.deviceId === cameraId)?.imageFilename || ''
        filename = filename?.split('.')[0] || '' // remove file extension, BE will add it
        const results = await prevPromise
        const imageResponse = await captureImage({ deviceId: cameraId, filename }).unwrap()
        return [...results, { deviceId: cameraId, imageFilename: imageResponse.filename }]
      }, Promise.resolve([]))

      callback(imageData)
    } catch (err) {
      trackEvent({
        eventName: cz.CAPTURE_IMAGE,
        error: err?.data?.error || 'Error capturing image. Please try again.',
      })
      callback([]) // Ensure the callback is invoked with an empty array on failure
    }
  }

  const debouncedRequestImages = useCallback(debounce(requestImages, 1000, { leading: true, trailing: false }), [
    selectedCameraIds,
    scaleCameras,
  ])

  return {
    requestImages: debouncedRequestImages,
    isLoadingRequestImages: isLoading,
    errorRequestingImages: error,
  }
}
